import { SearchIcon } from "@heroicons/react/solid"
import { HttpError, useOne } from "@refinedev/core";
import Button from "components/button"
import Input from "components/input"
import Select from "components/select";
import moment from "moment";
import { useState } from "react";
import Datepicker from "react-tailwindcss-datepicker";
import { twMerge } from "tailwind-merge";
import { PRIVATE_FLOW_TYPES, STATUS_OPTIONS } from "utills/constants";
import { UserType } from "interfaces";
import { canAccessByType } from "utills/helpers";

export const Filter = ({ currentFilterValues, setFilters}: {currentFilterValues: any, setFilters: any}) => {
  const isAdmin = canAccessByType(UserType.ADMIN);
  const [keyword, setKeyword] = useState(currentFilterValues.q);
  const [eventId, setEventId] = useState(currentFilterValues.event_id);
  const [isActive, setIsActive] = useState(currentFilterValues.is_active);
  const [privateFlowType, setPrivateFlowType] = useState(currentFilterValues.private_flow_type);
  const [createdDate, setCreatedDate] = useState({
    startDate: currentFilterValues.gte || "", 
    endDate: currentFilterValues.lte || ""
  });

  const { data, isLoading } = useOne<any[], HttpError>({
    resource: "content/dropdown/events?uncheck=true",
    id: "",
    meta: {
      resource: "data"
    }
  });

  const eventIds = (data?.data ?? []).map((event: {id: string; name_en: string}) => (
    { value: event.id, label: event.name_en }
  ));
  
  const _handleSubmitFilter = () => {
    setFilters([
      {field: "q", operator: "contains", value: keyword},
      {field: "event_id", operator: "eq", value: eventId},
      {field: "is_active", operator: "eq", value: isActive},
      ...(isAdmin ? [{field: "private_flow_type", operator: "eq", value: privateFlowType}] : []),
      {field: "gte", operator: "eq", value: createdDate.startDate ? moment(createdDate.startDate).format('YYYY-MM-DD') : ""},
      {field: "lte", operator: "eq", value: createdDate.endDate ? moment(createdDate.endDate).format('YYYY-MM-DD') : ""},
      {field: "page", operator: "eq", value: 1}
    ])
  }

  return (
    <div className="w-full flex flex-col gap-y-4">
      <div className="w-full flex flex-col gap-y-4 md:flex-row md:items-end md:gap-x-3">
        <div className="flex-1">
          <label htmlFor="q" className="block text-sm font-medium leading-6">
            Search
          </label>
          <div className="mt-2">
            <Input 
              className="px-2 py-2"
              label='Search'
              id='q'
              type="text"
              placeholder="Search by Passcode/Email"
              value={keyword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeyword(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter') {
                  _handleSubmitFilter();
                }
              }}
            />
          </div>
        </div>
        <div className="flex-1">
          <label htmlFor="event_id" className="block text-sm font-medium leading-6">
            Event name
          </label>
          <div className="mt-2">
            <Select
              options={eventIds}
              isLoading={isLoading}
              isClearable={true}
              id="event_id"
              placeholder="Select event name"
              className="py-0.5"
              value={eventIds.find((d) => d.value === eventId)}
              onChange={(selected: {label: string; value: string}) => {
                setEventId(selected?.value);
              }}
            />
          </div>
        </div>
      </div>
      <div className="w-full flex flex-col gap-y-4 md:flex-row md:items-end md:gap-x-3">
        <div className="flex-1">
          <label htmlFor="is_active" className="block text-sm font-medium leading-6">
            Status
          </label>
          <div className="mt-2">
            <Select
              options={STATUS_OPTIONS}
              isClearable={true}
              id="is_active"
              placeholder="Select status"
              className="py-0.5"
              value={STATUS_OPTIONS.find((d) => d.value === isActive)}
              onChange={(selected: {label: string; value: string}) => setIsActive(selected?.value)}
            />
          </div>
        </div>
        {isAdmin ? (
          <div className="flex-1">
            <label htmlFor="private_flow_type" className="block text-sm font-medium leading-6">
              Private flow type
            </label>
            <div className="mt-2">
              <Select
                options={PRIVATE_FLOW_TYPES}
                isClearable={true}
                id="private_flow_type"
                placeholder="Select private flow type"
                className="py-0.5"
                value={PRIVATE_FLOW_TYPES.find((d) => d.value === privateFlowType)}
                onChange={(selected: {label: string; value: string}) => setPrivateFlowType(selected?.value)}
              />
            </div>
          </div>
        ) : <></>}
        <div className="flex-1">
          <label htmlFor="created_date" className="block text-sm font-medium leading-6">
            Created Date
          </label>
          <div className="mt-2">
            <Datepicker
              key={"created_date"}
              value={createdDate}
              onChange={(value) => setCreatedDate({
                startDate: value?.startDate ? moment(value?.startDate).format('YYYY-MM-DD') : null,
                endDate: value?.endDate ? moment(value?.endDate).format('YYYY-MM-DD') : null
              })}
              displayFormat="DD/MM/YYYY"
              placeholder="Start date - End date"
              inputClassName="date-picker"
              popoverDirection="down"
              containerClassName={(className) => twMerge(className, "date-picker-container")}
            /> 
          </div>
        </div>
        <div className="shrink-0">
          <label htmlFor="search" className="block text-sm font-medium leading-6">
            <span className="sr-only">Search</span>
          </label>
          <div className="mt-2 w-full">
            <Button type="submit" className="w-full bg-white rounded-md px-3 text-brand-500 py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85" onClick={_handleSubmitFilter}>
              <SearchIcon className="w-3 h-4 text-brand-500 mr-2" /> Search
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
